import React, { useState, useEffect } from 'react';
import { useData } from '../../context/DataContext';
import { Building2, Plus, UserCircle } from 'lucide-react';

export default function PrincipalDepartments() {
    const { departments, refreshData } = useData();
    const [hodUsers, setHodUsers] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingDept, setEditingDept] = useState(null);
    const [formData, setFormData] = useState({ name: '', code: '', hodId: '' });

    useEffect(() => {
        // Fetch users who are HODs
        fetch('/api/users?role=HOD')
            .then(res => res.json())
            .then(data => setHodUsers(data));
    }, []);

    const handleSave = async (e) => {
        e.preventDefault();
        const url = editingDept
            ? `/api/departments/${editingDept.id}`
            : '/api/departments';

        const method = editingDept ? 'PUT' : 'POST';

        await fetch(url, {
            method: method,
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(formData)
        });

        await refreshData();
        setIsModalOpen(false);
        setEditingDept(null);
        setFormData({ name: '', code: '', hodId: '' });
    };

    const openEdit = (dept) => {
        setEditingDept(dept);
        setFormData({
            name: dept.name,
            code: dept.code,
            hodId: dept.hodId || '' // Pre-select current HOD
        });
        setIsModalOpen(true);
    };

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-2xl font-bold font-heading text-primary">Department Management</h1>
                    <p className="text-muted-foreground">Assign HODs to Branches</p>
                </div>
                <button
                    onClick={() => { setEditingDept(null); setFormData({ name: '', code: '', hodId: '' }); setIsModalOpen(true); }}
                    className="bg-primary text-primary-foreground px-4 py-2 rounded-lg flex items-center gap-2"
                >
                    <Plus size={18} /> Add Branch
                </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {departments.map(dept => (
                    <div key={dept.id} className="bg-card border border-border p-6 rounded-xl shadow-sm hover:shadow-md transition-all">
                        <div className="flex items-start justify-between mb-4">
                            <div className="h-12 w-12 bg-primary/10 rounded-lg flex items-center justify-center text-primary">
                                <Building2 size={24} />
                            </div>
                            <span className="bg-secondary px-2 py-1 rounded text-xs font-mono">{dept.code}</span>
                        </div>
                        <h3 className="text-lg font-bold">{dept.name}</h3>

                        <div className="mt-4 pt-4 border-t border-border">
                            <p className="text-xs text-muted-foreground mb-1 uppercase tracking-wide">Head of Department</p>
                            <div className="flex items-center gap-2">
                                <UserCircle size={20} className="text-muted-foreground" />
                                <span className={dept.hodName ? "font-medium" : "text-muted-foreground italic"}>
                                    {dept.hodName || "Not Assigned"}
                                </span>
                            </div>
                        </div>

                        <button
                            onClick={() => openEdit(dept)}
                            className="w-full mt-4 py-2 text-sm font-medium bg-secondary hover:bg-secondary/80 rounded-lg transition-colors"
                        >
                            Manage Settings
                        </button>
                    </div>
                ))}
            </div>

            {isModalOpen && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-card w-full max-w-md p-6 rounded-2xl shadow-xl">
                        <h2 className="text-xl font-bold mb-4">{editingDept ? 'Edit Department' : 'New Department'}</h2>
                        <form onSubmit={handleSave} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium mb-1">Branch Name</label>
                                <input
                                    className="w-full bg-secondary px-4 py-2 rounded-lg"
                                    value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })}
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">Code</label>
                                <input
                                    className="w-full bg-secondary px-4 py-2 rounded-lg"
                                    value={formData.code} onChange={e => setFormData({ ...formData, code: e.target.value })}
                                    required disabled={!!editingDept}
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">Assign HOD</label>
                                <select
                                    className="w-full bg-secondary px-4 py-2 rounded-lg"
                                    value={formData.hodId} onChange={e => setFormData({ ...formData, hodId: e.target.value })}
                                >
                                    <option value="">-- Select HOD User --</option>
                                    {hodUsers.map(u => (
                                        <option key={u.id} value={u.id}>{u.name} ({u.username})</option>
                                    ))}
                                </select>
                            </div>
                            <div className="flex gap-2 pt-4">
                                <button type="button" onClick={() => setIsModalOpen(false)} className="flex-1 py-2 bg-secondary rounded-lg">Cancel</button>
                                <button type="submit" className="flex-1 py-2 bg-primary text-primary-foreground rounded-lg">Save</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
